import { useMemo, type PointerEvent } from 'react';
import { useSetting, useTable } from '@core/store';
import { WriteOnly } from '@core/auth/access';
import { ChevronDown, ChevronRight, Crosshair } from 'lucide-react';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { Tooltip } from '@core/ui/Tooltip';
import { cn } from '@core/ui/cn';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { TOOLBAR_HEIGHT } from '../shared/geometry';
import { dateRange } from '@core/dates';
import { assigneeBackground } from '@core/people';
import type { Task } from '../shared/data';
import { QuestionItem } from '../shared/QuestionsPanel';
import { agendaCount, groupAgenda, type AgendaGroup } from '../shared/questions';

export type SideTab = 'backlog' | 'design' | 'agenda';
export type AgendaMode = 'open' | 'resolved';

/**
 * Повестка: вопросы по проектам в их порядке; режим и свёрнутые проекты — личные настройки. Открытые по умолчанию
 * развёрнуты, решённые свёрнуты (их много); хранятся отклонения, ключ — режим и проект. Решённые считаются, только когда их смотрят.
 */
export function useAgenda(allTasks: Map<string, Task>, projectOrder: { id: string; name: string }[]) {
  const questions = useTable('question');
  const [agendaMode, setAgendaMode] = useSetting<AgendaMode>('agenda.mode', 'open');
  const [flipped, setFlipped] = useSetting<string[]>('agenda.flipped', []);
  const agenda = useMemo(() => groupAgenda(questions.values(), allTasks, projectOrder), [questions, allTasks, projectOrder]);
  const resolved = useMemo(() => (agendaMode === 'resolved' ? groupAgenda(questions.values(), allTasks, projectOrder, 'closed') : []), [agendaMode, questions, allTasks, projectOrder]);
  const collapsed = useMemo(() => ({
    isOpen: (projectId: string) => flipped.includes(`${agendaMode}:${projectId}`) !== (agendaMode === 'open'),
    flip: (projectId: string) => { const k = `${agendaMode}:${projectId}`; setFlipped(flipped.includes(k) ? flipped.filter((x) => x !== k) : [...flipped, k]); },
  }), [flipped, setFlipped, agendaMode]);
  return { agenda, resolved, agendaMode, setAgendaMode, collapsed };
}

type Props = {
  tab: SideTab | null;
  onTab: (t: SideTab | null) => void;
  backlog: Task[];
  design: Task[];
  agenda: AgendaGroup<Task>[];
  resolved: AgendaGroup<Task>[];          // решённые вопросы — считаются, только когда их смотрят
  agendaMode: AgendaMode;
  onAgendaMode: (m: AgendaMode) => void;
  /** Свёрнутые проекты повестки: проверка и переключение (личная настройка экрана). */
  collapsed: { isOpen: (projectId: string) => boolean; flip: (projectId: string) => void };
  colors: Map<string, string[]>;
  projectNameOf: (t: Task) => string;
  onAdd: () => void;
  onOpen: (t: Task) => void;
  onOpenQuestions: (t: Task) => void;
  onPointerDown: (t: Task, e: PointerEvent<HTMLElement>) => void;
  dropTarget: boolean;
  menuFor: (t: Task) => MenuItem[];   // меню задачи — то же, что у бара
  onJump: (t: Task) => void;           // Design: показать бар на сетке
};

/** Две кнопки-вкладки: в тулбаре, когда панель закрыта, и в её шапке на том же месте, когда открыта. */
export function SideTabs({ tab, onTab, backlog, design, agenda }: Pick<Props, 'tab' | 'onTab' | 'backlog' | 'design' | 'agenda'>) {
  const items: [SideTab, string, number][] = [['backlog', 'Backlog', backlog.length], ['design', 'Design', design.length], ['agenda', 'Agenda', agenda.reduce((n, g) => n + agendaCount(g), 0)]];
  return (
    <SegmentedControl
      role="group" label="Side panel" value={tab}
      items={items.map(([key, label, n]) => ({ key, label, count: n, title: (tab === key ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`) + (key === 'design' ? ' — tasks in the visible period' : '') }))}
      onChange={(key) => onTab(tab === key ? null : key)}
    />
  );
}

// Правая панель: бэклог (карточки тянутся на сетку и обратно) и список «нужен дизайн».
export function SidePanel({ tab, onTab, backlog, design, agenda, resolved, agendaMode, onAgendaMode, collapsed, colors, projectNameOf, onAdd, onOpen, onOpenQuestions, onPointerDown, dropTarget, menuFor, onJump }: Props) {
  if (!tab) return null;
  const list = tab === 'backlog' ? backlog : tab === 'design' ? design : [];
  const agendaList = agendaMode === 'open' ? agenda : resolved;
  const { isOpen, flip } = collapsed;
  return (
    <aside
      data-backlog={tab === 'backlog' ? '' : undefined}
      // повестка — текст, ей нужно вдвое шире; сетка при этом остаётся видна
      className={`shrink-0 ${tab === 'agenda' ? 'w-160' : 'w-88'} bg-white rounded-lg shadow border border-t-0 border-gray-300 flex flex-col ${dropTarget ? 'ring-4 ring-accent ring-inset' : ''}`}
    >
      <div className="flex items-center gap-2 px-4 border-b border-gray-300" style={{ height: TOOLBAR_HEIGHT }}>
        {tab === 'agenda' && (
          <SegmentedControl role="tabs" label="Agenda" value={agendaMode} onChange={onAgendaMode}
            items={[{ key: 'open', label: 'Open', title: 'Open questions, in roadmap order' }, { key: 'resolved', label: 'Resolved', title: 'Resolved questions, newest first' }]} />
        )}
        <span className="ml-auto"><SideTabs tab={tab} onTab={onTab} backlog={backlog} design={design} agenda={agenda} /></span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2 scroller">
        {tab === 'agenda' && agendaList.map((g) => (
          <section key={g.project_id} className="flex flex-col gap-2" data-agenda-project={g.project_id}>
            <button type="button" className="min-h-0 mt-1 flex items-center gap-1 text-left font-bold text-sm text-gray-700 hover:text-gray-900" aria-expanded={isOpen(g.project_id)} onClick={() => flip(g.project_id)}>
              {isOpen(g.project_id) ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              {g.name}
              <span className="font-normal text-gray-500">· {agendaCount(g)}</span>
            </button>
            {isOpen(g.project_id) && g.tasks.map(({ task, questions }) => (
              <div key={task.id} className="flex flex-col gap-1">
                <button type="button" className="min-h-0 text-left text-sm font-semibold text-accent hover:underline" onClick={() => onOpenQuestions(task)} title="Open the task card">{task.title}</button>
                {questions.map((q) => <QuestionItem key={q.id} q={q} compact onOpenTask={() => onOpenQuestions(task)} />)}
              </div>
            ))}
          </section>
        ))}
        {tab === 'agenda' && agendaList.length === 0 && <p className="text-ink-muted p-2">{agendaMode === 'open' ? 'No open questions. Ask one from a task card.' : 'No resolved questions yet.'}</p>}
        {/* «+ New task» — первой карточкой списка, не в шапке: там вкладкам с трёхзначными счётчиками и так тесно */}
        {tab === 'backlog' && (
          <WriteOnly><button type="button" data-backlog-add className="min-h-0 shrink-0 rounded-md border-2 border-dashed border-accent/60 text-accent text-sm font-semibold px-3 py-2 hover:bg-accent/10" onClick={onAdd} title="New task in the backlog">+ New task</button></WriteOnly>
        )}
        {list.map((t) => (
          <Menu key={t.id} items={tab === 'design' ? [{ label: 'Show on the grid', onSelect: () => onJump(t), icon: Crosshair }, 'sep', ...menuFor(t)] : menuFor(t)}>
          <Tooltip title={t.title} lines={[projectNameOf(t), dateRange(t.start_date, t.end_date)]} hint={tab === 'backlog' ? 'Drag onto the grid · double-click to open' : 'Double-click to open'}>
          <div
            role="button" tabIndex={0}
            className={cn('shrink-0 rounded-md text-white text-sm font-bold px-3 py-2 select-none', tab === 'backlog' ? 'cursor-grab' : 'cursor-pointer')}
            style={{ background: assigneeBackground(colors.get(t.id) ?? []) }}
            onPointerDown={tab === 'backlog' ? (e) => onPointerDown(t, e) : undefined}
            onDoubleClick={() => onOpen(t)}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t); }}
          >
            {tab === 'design' && <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-300 mr-2" />}
            {t.title}
            {tab === 'design' && <span className="block text-xs font-normal opacity-90">{projectNameOf(t)} · {t.start_date}</span>}
          </div>
          </Tooltip>
          </Menu>
        ))}
        {tab !== 'agenda' && list.length === 0 && <p className="text-ink-muted p-2">{tab === 'backlog' ? 'Empty. Drag a task here to park it.' : 'Nothing needs design in the visible period.'}</p>}
      </div>
    </aside>
  );
}
